import {
  BookingIncrementMinutes,
  Campaign,
  Collab,
  DEFAULT_BOOKING_INCREMENT,
  FoodfluenceLocation,
} from '@foodfluence/shared';
import {
  addCalendarDays,
  calendarDayToUtcMidnightKey,
  getCalendarDatePartsInTimezone,
  getWeekdayForCalendarDay,
  parseCalendarDateTimeToUtc,
  toCalendarOrdinal,
} from '@foodfluence/shared';
import { getTimezone, TimezoneInfo } from './timezoneUtils';
import { getStartDate, getEndDate } from './dateUtils';
import { visitDeadline } from '@foodfluence/shared';
import { AvailableTimes } from '@foodfluence/shared';

/**
 * @deprecated Not used for open-hours scheduling — those slots come from the backend
 * (`getAvailableScheduleSlots`), which applies lead time and capacity. Kept for the
 * legacy client-side open-hours path only.
 */
const filterAndFormatTimeSlots = (
  timeSlots: string[],
  year: number,
  month: number,
  day: number,
  timezone: TimezoneInfo,
  apply24HourFilter: boolean = true
): { time: string; timezone: string; }[] => {
  if (apply24HourFilter) {
    const twentyFourHoursFromNow = new Date(Date.now() + 24 * 60 * 60 * 1000);
    return filterAndSortTimeOptions(timeSlots, year, month, day, timezone, twentyFourHoursFromNow);
  }

  // Otherwise, just apply basic sorting without date filtering
  return sortTimeOptions(timeSlots, timezone.display);
};

/**
 * Shared function to normalize date for map keys
 */
const normalizeDateKey = (year: number, month: number, day: number): string =>
  calendarDayToUtcMidnightKey(year, month, day);

/**
 * @deprecated Not used for open-hours scheduling — use backend `getAvailableScheduleSlots` instead.
 */
export const generateTimeOptionsForDateRange = (
  startDate: Date,
  endDate: Date | null,
  locations: FoodfluenceLocation[],
  campaign?: Campaign,
  locationId?: string
): Map<string, { time: string; timezone: string; }[]> => {
  const timeOptions = new Map();
  if (!startDate || !campaign?.openHours) return timeOptions;

  // If endDate is null, use 3 months from current date instead of from start date
  const finalEndDate = endDate || new Date(Date.now() + 90 * 24 * 60 * 60 * 1000);
  const timezone = getTimezone(locationId, locations);

  let { year, month, day } = getCalendarDatePartsInTimezone(startDate, timezone.value);
  const endParts = getCalendarDatePartsInTimezone(finalEndDate, timezone.value);
  const endOrdinal = toCalendarOrdinal(endParts.year, endParts.month, endParts.day);

  while (toCalendarOrdinal(year, month, day) <= endOrdinal) {
    const dayOfWeek = getWeekdayForCalendarDay(year, month, day, timezone.value);
    const availableHours = campaign.openHours[dayOfWeek] || [];

    if (availableHours.length > 0) {
      const processedTimeOptions = filterAndFormatTimeSlots(
        availableHours,
        year,
        month,
        day,
        timezone
      );

      if (processedTimeOptions.length > 0) {
        const dateKey = calendarDayToUtcMidnightKey(year, month, day);
        timeOptions.set(dateKey, processedTimeOptions);
      }
    }
    ({ year, month, day } = addCalendarDays(year, month, day, 1));
  }
  return timeOptions;
};

const filterAndSortTimeOptions = (
  availableHours: string[], 
  year: number,
  month: number,
  day: number,
  timezone: TimezoneInfo, 
  earliestAvailableTime: Date
): { time: string; timezone: string; }[] => {
  const filteredHours = availableHours.filter(time => {
    const localTime = parseCalendarDateTimeToUtc(year, month, day, time, timezone.value);
    return localTime > earliestAvailableTime;
  });

  return sortTimeOptions(filteredHours, timezone.display);
};

const sortTimeOptions = (hours: string[], timezone: string): { time: string; timezone: string; }[] => {
  return hours
    .sort((a, b) => {
      const [aTime, aModifier] = a.split(' ');
      const [bTime, bModifier] = b.split(' ');
      const [aHours, aMinutes] = aTime.split(':').map(Number);
      const [bHours, bMinutes] = bTime.split(':').map(Number);

      // Convert to minutes since midnight, handling 12 AM/PM specially
      const getMinutes = (hours: number, minutes: number, modifier: string) => {
        if (hours === 12) {
          return modifier === 'AM' ? minutes : (12 * 60) + minutes;
        }
        return ((modifier === 'PM' ? hours + 12 : hours) * 60) + minutes;
      };

      const aTotalMinutes = getMinutes(aHours, aMinutes, aModifier);
      const bTotalMinutes = getMinutes(bHours, bMinutes, bModifier);
      
      return aTotalMinutes - bTotalMinutes;
    })
    .map(time => ({
      time,
      timezone
    }));
};

export const createTimeOptionsInAvailableDateTimeRange = (
  startTime: string,
  endTime: string,
  timezone: TimezoneInfo,
  year: number,
  month: number,
  day: number,
  incrementMinutes: BookingIncrementMinutes = DEFAULT_BOOKING_INCREMENT
): { time: string; timezone: string; }[] => {
  const timeSlots = generateTimeStrings(startTime, endTime, incrementMinutes);
  return filterAndFormatTimeSlots(timeSlots, year, month, day, timezone, false);
};

// The increment comes from the range itself, set by the brand in the dashboard. Unlike
// open-hours scheduling — where the brand's stored labels *are* the slots — this mode stores a
// start/end range, so the spacing has to be applied here. Ranges saved before increments
// shipped carry none and fall back to the default.
const generateTimeStrings = (startTime: string, endTime: string, intervalMinutes: number = DEFAULT_BOOKING_INCREMENT): string[] => {
  // No end means the brand opened a single bookable time — often a one-off event rather than a
  // window. Without this the empty string parses to NaN and the loop below yields nothing, so the
  // creator would be offered no times at all for that date.
  if (!endTime) {
    return startTime ? [startTime] : [];
  }

  // Parse start time
  const [startTimePart, startPeriod] = startTime.split(' ');
  const [startHours, startMinutes] = startTimePart.split(':').map(Number);
  
  // Parse end time
  const [endTimePart, endPeriod] = endTime.split(' ');
  const [endHours, endMinutes] = endTimePart.split(':').map(Number);
  
  // Convert to 24-hour format for easier calculation
  const convertTo24Hour = (hours: number, period: string) => {
    if (period === 'AM' && hours === 12) return 0;
    if (period === 'PM' && hours !== 12) return hours + 12;
    return hours;
  };
  
  const start24Hours = convertTo24Hour(startHours, startPeriod);
  const end24Hours = convertTo24Hour(endHours, endPeriod);
  
  // Convert to minutes since midnight
  const startTotalMinutes = start24Hours * 60 + startMinutes;
  let endTotalMinutes = end24Hours * 60 + endMinutes;
  
  // Handle overnight case (e.g., 8 PM to 2 AM)
  if (endTotalMinutes <= startTotalMinutes) {
    endTotalMinutes += 24 * 60; // Add 24 hours
  }
  
  const timeSlots: string[] = [];
  
  // Generate time slots at specified intervals
  for (let minutes = startTotalMinutes; minutes < endTotalMinutes; minutes += intervalMinutes) {
    const actualMinutes = minutes % (24 * 60); // Handle day wrap-around
    const hours24 = Math.floor(actualMinutes / 60);
    const mins = actualMinutes % 60;
    
    // Convert back to 12-hour format
    const period = hours24 < 12 ? 'AM' : 'PM';
    const hours12 = hours24 === 0 ? 12 : (hours24 > 12 ? hours24 - 12 : hours24);
    
    // Format with leading zeros
    const formattedTime = `${hours12.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')} ${period}`;
    timeSlots.push(formattedTime);
  }
  
  return timeSlots;
};

/**
 * @deprecated Not used for open-hours scheduling — use backend `getAvailableScheduleSlots` instead.
 */
export const processCampaignOpenHours = (
  campaign: Campaign,
  collab: Collab,
  locations: FoodfluenceLocation[],
  locationId?: string
): Map<string, { time: string; timezone: string; }[]> => {
  const startDate = getStartDate(campaign);
  const endDate = getEndDate(campaign, collab);
  return generateTimeOptionsForDateRange(startDate, endDate, locations, campaign, locationId);
};

/**
 * Generate time options based on specific date/time intervals
 */
export const processAvailableTimeIntervals = (
  availableTimes: AvailableTimes,
  locations: FoodfluenceLocation[],
  locationId?: string,
  /** Go-live visit deadline: no slots on days after it (venue calendar). */
  lastDay?: Date | null,
): Map<string, { time: string; timezone: string; }[]> => {
  const timeOptionsMap = new Map<string, { time: string; timezone: string; }[]>();
  
  Object.entries(availableTimes).forEach(([dateString, timeSlots]) => {
    const timezone = getTimezone(locationId, locations);
    
    // Parse date string carefully to avoid timezone issues
    // Split "2025-06-26" into parts and create date in local timezone
    const [year, month, day] = dateString.split('-').map(Number);

    const todayParts = getCalendarDatePartsInTimezone(new Date(), timezone.value);
    const todayOrdinal = toCalendarOrdinal(todayParts.year, todayParts.month, todayParts.day);
    if (toCalendarOrdinal(year, month, day) < todayOrdinal) return;
    if (lastDay) {
      const last = getCalendarDatePartsInTimezone(lastDay, timezone.value);
      if (toCalendarOrdinal(year, month, day) > toCalendarOrdinal(last.year, last.month, last.day)) return;
    }

    const dateKey = normalizeDateKey(year, month, day);
    
    // Generate time options for all time slots on this date
    const allTimeOptionsForDate: { time: string; timezone: string; }[] = [];
    
    timeSlots.forEach((slot) => {
      const timeOptionsForSlot = createTimeOptionsInAvailableDateTimeRange(
        slot.startTime,
        slot.endTime,
        timezone,
        year,
        month,
        day,
        slot.incrementMinutes ?? DEFAULT_BOOKING_INCREMENT
      );
      allTimeOptionsForDate.push(...timeOptionsForSlot);
    });
    
    // Remove duplicates and sort the final combined list
    const uniqueTimeOptions = allTimeOptionsForDate.filter((option, index, self) => 
      index === self.findIndex(t => t.time === option.time)
    );
    
    // Sort the combined time options since they come from multiple intervals
    const sortedTimeOptions = sortTimeOptions(
      uniqueTimeOptions.map(opt => opt.time), 
      timezone.display
    );
    
    if (sortedTimeOptions.length > 0) {
      timeOptionsMap.set(dateKey, sortedTimeOptions);
    }
  });
  
  return timeOptionsMap;
};

/**
 * Main function to get available dates and times based on data source
 */
export const getAvailableDatesAndTimes = (
  hasSpecificTimeIntervals: boolean,
  campaign: Campaign,
  collab: Collab,
  locations: FoodfluenceLocation[],
  availableTimes?: AvailableTimes,
  locationId?: string,
): Map<string, { time: string; timezone: string; }[]> => {
  if (hasSpecificTimeIntervals && availableTimes && Object.keys(availableTimes).length > 0) {
    const timezone = getTimezone(locationId, locations).value;
    return processAvailableTimeIntervals(availableTimes, locations, locationId, visitDeadline(collab, campaign, timezone));
  }
  // Open-hours campaigns never reach here in production — useSchedulingAvailability fetches
  // slots from the backend when usesOpenHoursScheduling is true.
  return processCampaignOpenHours(campaign, collab, locations, locationId);
};